import { Fragment } from 'react';

import { RecordDefs, RecordSection } from '@/components/admin/detail/RecordSheet';
import { getRequestTypeConfig } from '@/data/requestsConfig';

interface RequestSectionsProps {
  type: string;
  payload: Record<string, unknown>;
}

const SKIP_KEYS = new Set(['consent', 'termsAccepted', 'compliance']);
const FULL_WIDTH_FIELDS = new Set(['email', 'pickupLocation', 'notes', 'address', 'eventPurpose']);

function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
  if (Array.isArray(value)) return value.length > 0 ? value.map(formatValue).join(', ') : '—';
  if (typeof value === 'number' || typeof value === 'bigint') return String(value);
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
}

// Renders a service request's payload as ruled record sections, grouped per the
// request-type config. Unmapped keys fall into an "Outros Campos" section.
export function RequestSections({ type, payload }: RequestSectionsProps) {
  const config = getRequestTypeConfig(type);
  const configuredFields = new Set(config.sections.flatMap((s) => s.fields));

  const sections = config.sections
    .map((section) => ({
      title: section.title,
      items: section.fields
        .filter((k) => !SKIP_KEYS.has(k) && payload[k] !== undefined && payload[k] !== '')
        .map((k) => ({
          label: config.labels[k] ?? k,
          value: formatValue(payload[k]),
          full: FULL_WIDTH_FIELDS.has(k),
        })),
    }))
    .filter((s) => s.items.length > 0);

  const extraItems = Object.entries(payload)
    .filter(([k]) => !configuredFields.has(k) && !SKIP_KEYS.has(k))
    .map(([k, v]) => ({ label: config.labels[k] ?? k, value: formatValue(v), full: false }));

  return (
    <Fragment>
      {sections.map((section) => (
        <RecordSection key={section.title} label={section.title}>
          <RecordDefs items={section.items} />
        </RecordSection>
      ))}
      {extraItems.length > 0 && (
        <RecordSection label="Outros campos">
          <RecordDefs items={extraItems} />
        </RecordSection>
      )}
    </Fragment>
  );
}
